// ============================================================
// CUSTOMER MANAGEMENT PAGE
// ============================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, ExternalLink } from 'lucide-react';
import { Table, Badge, Button, SearchInput, Select, Pagination, Modal, Avatar, StatCard } from '../components/ui';
import { usePagination, useSearch } from '../hooks';
import { getCustomers } from '../services/mockData';
import { adminApi } from '../services/apiService';
import { formatDate, formatCurrency, downloadCSV } from '../utils/helpers';
import { UserCheck, TrendingUp, Wallet } from 'lucide-react';
import { toast } from 'react-toastify';

const CustomerManagement = () => {
  const navigate = useNavigate();
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    adminApi.getCustomers({}, getCustomers)
      .then(res => { setAll(Array.isArray(res) ? res : (res?.items || [])); setLoading(false); })
      .catch(e => { setError(e.message || 'Failed to load data'); setLoading(false); });
  }, []);

  const { query, setQuery, filtered } = useSearch(all, ['name', 'email', 'phone', 'city']);
  const final = statusFilter ? filtered.filter(c => c.status === statusFilter) : filtered;
  const { currentPage, totalPages, paginated, goTo } = usePagination(final);

  const toggleStatus = async (c) => {
    // active → inactive (disabled), inactive/suspended → active (enabled)
    const newStatus = c.status === 'active' ? 'inactive' : 'active';
    try {
      await adminApi.toggleCustomerStatus(c.id, newStatus);
      setAll(p => p.map(x => x.id === c.id ? { ...x, status: newStatus } : x));
      toast.success(`Customer ${newStatus === 'active' ? 'enabled' : 'disabled'} successfully`);
    } catch (e) {
      toast.error(e.message || 'Failed to update status');
    }
  };

  const columns = [
    {
      key: 'name', label: 'Customer',
      render: (v, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar name={v || row.email || 'N'} />
          <div>
            <div style={{ fontWeight: 600 }}>{v || '—'}</div>
            {row.email && <div style={{ fontSize: 12, color: 'var(--admin-text-muted)' }}>{row.email}</div>}
          </div>
        </div>
      ),
    },
    { key: 'phone', label: 'Phone', render: v => v || '—' },
    { key: 'city',  label: 'City',  render: v => v || '—' },
    { key: 'totalOrders',  label: 'Orders',      render: v => <b>{v ?? 0}</b> },
    { key: 'totalSpent',   label: 'Total Spent',  render: v => formatCurrency(v ?? 0) },
    { key: 'walletBalance',label: 'Wallet',       render: v => formatCurrency(v ?? 0) },
    { key: 'rewardPoints', label: 'Points',       render: v => <span style={{ color: '#F59E0B', fontWeight: 700 }}>⭐ {v ?? 0}</span> },
    { key: 'status', label: 'Status', render: v => <Badge status={v} /> },
    { key: 'lastOrder', label: 'Last Order', render: v => v ? formatDate(v) : 'N/A' },
    {
      key: 'id', label: 'Actions',
      render: (_, row) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <Button variant="primary" size="sm" icon={<ExternalLink size={12} />} onClick={() => navigate(`/admin/customers/${row.id}`)}>Details</Button>
          <Button variant="info" size="sm" onClick={() => setSelected(row)}>Quick View</Button>
          <Button
            variant={row.status === 'active' ? 'danger' : 'success'}
            size="sm"
            onClick={() => toggleStatus(row)}
          >
            {row.status === 'active' ? 'Disable' : 'Enable'}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Customer Management</h1>
          <p className="admin-page-subtitle">
            All registered users from the Biryani Customer App &nbsp;·&nbsp;
            <strong>{all.length}</strong> total customers
          </p>
        </div>
        <div className="admin-page-actions">
          <Button variant="secondary" icon={<Download size={14} />} onClick={() => downloadCSV(all, 'customers')}>Export</Button>
        </div>
      </div>

      <div className="admin-stat-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 20 }}>
        <StatCard label="Total Customers" value={all.length} icon={<UserCheck size={18} />} color="#3B82F6" />
        <StatCard label="Active" value={all.filter(c => c.status === 'active').length} icon={<TrendingUp size={18} />} color="#10B981" />
        <StatCard label="Total Wallet Balance" value={formatCurrency(all.reduce((s, c) => s + (c.walletBalance || 0), 0))} icon={<Wallet size={18} />} color="#8B5CF6" />
        <StatCard label="Total Spent" value={formatCurrency(all.reduce((s, c) => s + (c.totalSpent || 0), 0))} icon={<TrendingUp size={18} />} color="#E85D04" />
      </div>

      <div className="admin-table-wrapper">
        <div className="admin-table-toolbar">
          <SearchInput value={query} onChange={setQuery} placeholder="Search customers..." />
          <Select value={statusFilter} onChange={setStatusFilter} placeholder="All Status"
            options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]} />
        </div>
        <Table columns={columns} data={paginated} loading={loading} />
        <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={final.length} itemsPerPage={10} onPageChange={goTo} />
      </div>

      <Modal open={!!selected} onClose={() => setSelected(null)} title="Customer Details" size="md"
        footer={<Button variant="secondary" onClick={() => setSelected(null)}>Close</Button>}>
        {selected && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, padding: 16, background: 'var(--admin-bg)', borderRadius: 10 }}>
              <Avatar name={selected.name} size={56} />
              <div>
                <div style={{ fontSize: 18, fontWeight: 800 }}>{selected.name}</div>
                <div style={{ fontSize: 13, color: 'var(--admin-text-muted)' }}>{selected.email}</div>
                <Badge status={selected.status} />
              </div>
            </div>
            {[
              ['Customer ID', selected.id], ['Phone', selected.phone], ['City', selected.city],
              ['Total Orders', selected.totalOrders], ['Total Spent', formatCurrency(selected.totalSpent)],
              ['Wallet Balance', formatCurrency(selected.walletBalance)], ['Reward Points', `⭐ ${selected.rewardPoints}`],
              ['Joined', formatDate(selected.joinedAt)], ['Last Order', formatDate(selected.lastOrder)],
            ].map(([l, v]) => (
              <div key={l} className="admin-info-row">
                <span className="admin-info-row-label">{l}</span>
                <span className="admin-info-row-value">{v}</span>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CustomerManagement;
